// frontend/src/app/features/admin/adminApi.js
import http from "../../api/http.js";

// ============================================================
// Trade Request Management Functions
// ============================================================

/**
 * List all trade requests with filtering and pagination
 * @param {Object} params - Query parameters
 * @param {string} params.status - Filter by status (PENDING, APPROVED, REJECTED, RESOLVED, CANCELLED)
 * @param {string} params.market - Filter by market symbol
 * @param {string} params.direction - Filter by direction (UP, DOWN)
 * @param {string} params.from - Filter by created_at >= from
 * @param {string} params.to - Filter by created_at <= to
 * @param {number} params.limit - Items per page (default: 20, max: 200)
 * @param {number} params.offset - Pagination offset
 */
export async function adminListTradeRequests(params = {}) {
  const res = await http.get("/admin/trade-requests", { params });
  return res.data; // expected: { items, total, limit, offset }
}

/**
 * Get pending trades that are approved and waiting for admin decision
 * These are trades where resolve_at <= now and is_settled = false
 */
export async function adminGetPendingTrades() {
  const res = await http.get("/admin/trade-requests/pending");
  return res.data; // expected: Array of trade requests
}

/**
 * Approve a trade request (PENDING -> APPROVED)
 * @param {string} tradeRequestId - UUID of the trade
 * @param {string} note - Optional admin note
 */
export async function adminApproveTradeRequest(tradeRequestId, note = "") {
  const res = await http.post(`/admin/trade-requests/${tradeRequestId}/approve`, {
    note,
  });
  return res.data; // expected: { ok, id, status, resolveAt, entryPrice }
}

/**
 * Reject a trade request (PENDING -> REJECTED)
 * @param {string} tradeRequestId - UUID of the trade
 * @param {string} reason - Rejection reason (required, min 3 chars)
 */
export async function adminRejectTradeRequest(
  tradeRequestId,
  reason = "Rejected by moderator"
) {
  const res = await http.post(`/admin/trade-requests/${tradeRequestId}/reject`, {
    reason,
  });
  return res.data; // expected: { ok, id, status }
}

/**
 * Set the result for an approved trade (APPROVED -> RESOLVED)
 * This settles the trade and transfers funds immediately
 * @param {string} tradeRequestId - UUID of the trade
 * @param {Object} data - Result data
 * @param {string} data.outcome - WIN, LOSE, or PUSH
 * @param {string} data.note - Optional admin note
 */
export async function adminSetTradeResult(
  tradeRequestId,
  { outcome, note = "" }
) {
  const res = await http.post(`/admin/trade-requests/${tradeRequestId}/result`, {
    outcome,
    note,
  });
  return res.data; // expected: { ok, id, status, outcome, payoutAmount, resolvedAt }
}

// ============================================================
// Wallet Management Functions
// ============================================================

/**
 * Get treasury wallet balances
 */
export async function getTreasuryBalances() {
  const res = await http.get("/admin/wallet/treasury");
  return res.data;
}

/**
 * Get balances for all users with filtering
 * @param {Object} params - Query parameters
 * @param {string} params.userId - Filter by user ID
 * @param {string} params.asset - Filter by asset
 * @param {string} params.network - Filter by network
 */
export async function getUserBalances(params = {}) {
  const res = await http.get("/admin/wallet/users/balances", { params });
  return res.data;
}

/**
 * Get wallet balance summary statistics
 */
export async function getBalanceSummary() {
  const res = await http.get("/admin/wallet/summary");
  return res.data;
}

/**
 * Adjust user balance (admin only)
 * @param {Object} data - Adjustment data
 * @param {string} data.userId - User ID
 * @param {string} data.asset - Asset symbol
 * @param {string} data.network - Network name
 * @param {number} data.amount - Amount to adjust (can be negative)
 * @param {number} data.deltaLocked - Locked amount adjustment (optional)
 * @param {string} data.reason - Reason for adjustment
 */
export async function adjustUserBalance(data) {
  const payload = {
    user_id: data.userId,
    asset: data.asset,
    network: data.network,
    delta_available: data.amount,
    delta_locked: data.deltaLocked || 0,
    reason: data.reason,
  };
  
  const res = await http.post("/admin/wallet/balances/adjust", payload);
  return res.data;
}

// ============================================================
// Withdrawal Management Functions
// ============================================================

/**
 * Get pending withdrawal requests
 * @param {Object} params - Query parameters
 * @param {number} params.limit - Items per page
 * @param {number} params.offset - Pagination offset
 */
export async function getPendingWithdrawals(params = {}) {
  const res = await http.get("/admin/wallet/withdrawals/pending", { params });
  return res.data;
}

/**
 * Approve a withdrawal request
 * @param {string} withdrawalId - UUID of the withdrawal
 * @param {Object} data - Approval data
 * @param {string} data.note - Optional admin note
 * @param {boolean} data.broadcastNow - Whether to broadcast immediately
 */
export async function approveWithdrawal(withdrawalId, data = {}) {
  const res = await http.post(`/admin/wallet/withdrawals/${withdrawalId}/approve`, {
    note: data.note || null,
    broadcastNow: !!data.broadcastNow,
  });
  return res.data;
}

/**
 * Reject a withdrawal request
 * @param {string} withdrawalId - UUID of the withdrawal
 * @param {Object} data - Rejection data
 * @param {string} data.reason - Rejection reason (required)
 */
export async function rejectWithdrawal(withdrawalId, data = {}) {
  const res = await http.post(`/admin/wallet/withdrawals/${withdrawalId}/reject`, {
    reason: data.reason,
  });
  return res.data;
}

// ============================================================
// Trade History Functions
// ============================================================

/**
 * Get all trade history (admin only)
 * @param {Object} params - Query parameters
 * @param {string} params.userId - Filter by user ID
 * @param {string} params.outcome - Filter by outcome (WIN, LOSE, PUSH)
 * @param {string} params.status - Filter by status
 * @param {string} params.market - Filter by market
 * @param {string} params.direction - Filter by direction (UP, DOWN)
 * @param {string} params.from - Filter by created_at >= from
 * @param {string} params.to - Filter by created_at <= to
 * @param {boolean} params.settledOnly - Only show settled trades
 * @param {number} params.limit - Items per page
 * @param {number} params.offset - Pagination offset
 */
export async function adminGetTradeHistory(params = {}) {
  const res = await http.get("/admin/trade-history", { params });
  return res.data; // expected: { items, total, limit, offset }
}

// ============================================================
// User Management Functions
// ============================================================

/**
 * Get list of users (admin only)
 * @param {Object} params - Query parameters
 * @param {string} params.search - Search by email or account ID
 * @param {string} params.role - Filter by role
 * @param {string} params.kycStatus - Filter by KYC status
 * @param {number} params.limit - Items per page
 * @param {number} params.offset - Pagination offset
 */
export async function adminListUsers(params = {}) {
  const res = await http.get("/admin/users", { params });
  return res.data;
}

/**
 * Get user details by ID
 * @param {string} userId - User ID
 */
export async function adminGetUser(userId) {
  const res = await http.get(`/admin/users/${userId}`);
  return res.data;
}

/**
 * Update user details (admin only)
 * @param {string} userId - User ID
 * @param {Object} data - Update data
 * @param {string} data.role - USER, MODERATOR, ADMIN
 * @param {string} data.kycStatus - UNVERIFIED, PENDING, VERIFIED, REJECTED
 * @param {boolean} data.isActive - Whether user is active
 * @param {boolean} data.isSuspended - Whether user is suspended
 * @param {string} data.suspendedReason - Reason for suspension
 */
export async function adminUpdateUser(userId, data) {
  const res = await http.patch(`/admin/users/${userId}`, data);
  return res.data;
}

/**
 * Suspend a user
 * @param {string} userId - User ID
 * @param {string} reason - Suspension reason
 */
export async function adminSuspendUser(userId, reason) {
  const res = await http.post(`/admin/users/${userId}/suspend`, { reason });
  return res.data;
}

/**
 * Activate/unsuspend a user
 * @param {string} userId - User ID
 */
export async function adminActivateUser(userId) {
  const res = await http.post(`/admin/users/${userId}/activate`);
  return res.data;
}

// ============================================================
// Chat Management Functions (NEW)
// ============================================================

/**
 * List all chat threads with filtering
 * GET /admin/chat/threads
 * @param {Object} params - Query parameters
 * @param {string} params.status - Filter by status (ALL, UNASSIGNED, ASSIGNED)
 * @param {number} params.limit - Items per page (default: 50)
 * @param {number} params.offset - Pagination offset
 */
export async function adminListChatThreads(params = {}) {
  const res = await http.get("/admin/chat/threads", { params });
  return res.data; // { items, total, limit, offset }
}

/**
 * Get a specific thread details
 * GET /admin/chat/threads/{threadId}
 * @param {string} threadId - Thread ID
 */
export async function adminGetChatThread(threadId) {
  const res = await http.get(`/admin/chat/threads/${encodeURIComponent(threadId)}`);
  return res.data;
}

/**
 * Assign a thread to current moderator/admin
 * POST /admin/chat/threads/{threadId}/assign
 * @param {string} threadId - Thread ID
 * @param {Object} payload - Optional payload (e.g., { moderatorId: '...' })
 */
export async function adminAssignChatThread(threadId, payload = null) {
  const url = `/admin/chat/threads/${encodeURIComponent(threadId)}/assign`;
  const res = payload ? await http.post(url, payload) : await http.post(url);
  return res.data; // { threadId, moderatorId }
}

/**
 * Unassign a thread (remove moderator)
 * POST /admin/chat/threads/{threadId}/unassign
 * @param {string} threadId - Thread ID
 */
export async function adminUnassignChatThread(threadId) {
  const res = await http.post(`/admin/chat/threads/${encodeURIComponent(threadId)}/unassign`);
  return res.data; // { threadId, moderatorId: null }
}

/**
 * Get messages for a specific thread (admin view)
 * GET /admin/chat/messages?threadId=...&cursor=...&limit=...
 * @param {Object} params - Query parameters
 * @param {string} params.threadId - Thread ID (required)
 * @param {string} params.cursor - Pagination cursor (optional)
 * @param {number} params.limit - Items per page (default: 50)
 */
export async function adminListMessages({ threadId, cursor = null, limit = 50 }) {
  const res = await http.get("/admin/chat/messages", {
    params: { threadId, cursor, limit },
  });
  return res.data; // { items, nextCursor }
}

// ============================================================
// System/Status Functions
// ============================================================

/**
 * Get system health status
 */
export async function getSystemStatus() {
  const res = await http.get("/admin/system/status");
  return res.data;
}

/**
 * Get system metrics
 */
export async function getSystemMetrics() {
  const res = await http.get("/admin/system/metrics");
  return res.data;
}

// ============================================================
// Export all functions as a single object for convenience
// ============================================================

export const adminApi = {
  // Trade requests
  listTradeRequests: adminListTradeRequests,
  getPendingTrades: adminGetPendingTrades,
  approveTrade: adminApproveTradeRequest,
  rejectTrade: adminRejectTradeRequest,
  setTradeResult: adminSetTradeResult,
  getTradeHistory: adminGetTradeHistory,
  
  // Wallet
  getTreasuryBalances,
  getUserBalances,
  getBalanceSummary,
  adjustUserBalance,
  getPendingWithdrawals,
  approveWithdrawal,
  rejectWithdrawal,
  
  // Users
  listUsers: adminListUsers,
  getUser: adminGetUser,
  updateUser: adminUpdateUser,
  suspendUser: adminSuspendUser,
  activateUser: adminActivateUser,
  
  // Chat (NEW)
  listChatThreads: adminListChatThreads,
  getChatThread: adminGetChatThread,
  assignChatThread: adminAssignChatThread,
  unassignChatThread: adminUnassignChatThread,
  listChatMessages: adminListMessages,
  
  // System
  getSystemStatus,
  getSystemMetrics,
};
